import React, { useEffect, useId, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Search, X, Plus, Loader2, Lightbulb, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { findMatchRange, normalizeSkillText } from "@/lib/skillMatch";
import type { Skill } from "@/types/consultant-application";

/** A search hit. `matched_alias` says which alias found it, if not the name. */
export interface SkillSearchResult extends Skill {
  matched_alias?: string | null;
  /** How the server matched it, e.g. `alias_exact` (backend#992). */
  match_type?: string | null;
}

/** What suggesting a skill led to (backend#992). */
export type SkillSuggestOutcome =
  | { status: "existing"; skill: Skill }
  | { status: "pending"; text: string; alreadySuggested?: boolean };

/**
 * What the picker offers when nothing fits the typed text, chosen by role:
 * consultants and applicants suggest a skill for review, admins create one
 * after a "similar skills exist" check (#1061).
 */
export type SkillNoMatchAction =
  | { kind: "suggest"; suggest: (text: string) => Promise<SkillSuggestOutcome> }
  | {
      kind: "create";
      findSimilar: (name: string) => Promise<Skill[]>;
      create: (name: string) => Promise<Skill>;
    };

interface SkillsAutocompleteProps {
  selectedSkills: Skill[];
  onSkillsChange: (skills: Skill[]) => void;
  /** Server search over names and aliases. Results are shown as ranked. */
  onSearchSkills: (query: string) => Promise<SkillSearchResult[]>;
  maxSkills?: number;
  placeholder?: string;
  className?: string;
  /** Visible label of the search field. */
  label?: string;
  /** Appends "(selected/max)" to the label. */
  showCount?: boolean;
  /** Replaces the default hint under the field. */
  hint?: string;
  /**
   * Heading above the chosen skills. Defaults to "Selected Skills", which suits
   * the signup form; a profile page where the skills are already saved reads
   * better as "Your skills".
   */
  selectedLabel?: string;
  /** Disables the input and remove buttons while a save is in flight. */
  disabled?: boolean;
  /**
   * Renders the built-in list of chosen skills. Callers that already display
   * the selection themselves — the admin assignment form paints its own
   * three-state chips — turn this off to avoid showing it twice.
   */
  showSelectedList?: boolean;
  /** Offered at the bottom of the list when no result is the typed skill. */
  noMatchAction?: SkillNoMatchAction;
  /** Suggested skills still awaiting an admin's review, shown as chips. */
  pendingSuggestions?: string[];
}

/** Search waits this long after the last keystroke. */
const SEARCH_DEBOUNCE_MS = 300;

const Highlighted: React.FC<{ text: string; query: string }> = ({ text, query }) => {
  const range = findMatchRange(text, query);
  if (!range) {
    return <>{text}</>;
  }
  const [start, end] = range;
  return (
    <>
      {text.slice(0, start)}
      <mark className="bg-transparent font-semibold text-foreground underline underline-offset-2">
        {text.slice(start, end)}
      </mark>
      {text.slice(end)}
    </>
  );
};

const SimilarSkillsWarning: React.FC<{
  name: string;
  skills: Skill[];
  busy: boolean;
  onUse: (skill: Skill) => void;
  onCreate: () => void;
  onCancel: () => void;
}> = ({ name, skills, busy, onUse, onCreate, onCancel }) => (
  <div
    role="group"
    aria-label="Similar skills exist"
    className="space-y-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm dark:border-amber-700 dark:bg-amber-950/40"
  >
    <p className="flex items-start gap-2 text-amber-900 dark:text-amber-200">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        Similar skills exist: {skills.map(s => s.name).join(", ")}. Use one of them instead of
        creating &ldquo;{name}&rdquo;?
      </span>
    </p>
    <div className="flex flex-wrap gap-2">
      {skills.map(skill => (
        <Button
          key={skill.id}
          type="button"
          size="sm"
          variant="outline"
          onClick={() => onUse(skill)}
          disabled={busy}
        >
          Use {skill.name}
        </Button>
      ))}
      <Button type="button" size="sm" onClick={onCreate} disabled={busy}>
        Create &ldquo;{name}&rdquo; anyway
      </Button>
      <Button type="button" size="sm" variant="ghost" onClick={onCancel} disabled={busy}>
        Cancel
      </Button>
    </div>
  </div>
);

const SelectedSkillChips: React.FC<{
  label: string;
  skills: Skill[];
  pending: string[];
  disabled: boolean;
  onRemove: (skillId: number) => void;
}> = ({ label, skills, pending, disabled, onRemove }) => {
  if (skills.length === 0 && pending.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium leading-none">{label}</p>
      <ul className="flex flex-wrap gap-2" aria-label={label}>
        {skills.map(skill => (
          <li key={skill.id}>
            <Badge className="flex items-center gap-2 py-1 px-3 text-sm bg-primary text-primary-foreground hover:bg-primary/90">
              {skill.name}
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onRemove(skill.id)}
                disabled={disabled}
                // 24×24 tap target around the 12px icon; the negative
                // margin keeps the badge the same size (#1038).
                className="h-auto w-auto p-1.5 -m-1.5 hover:bg-transparent text-primary-foreground hover:text-primary-foreground/80"
              >
                <X className="w-3 h-3" aria-hidden="true" />
                <span className="sr-only">Remove {skill.name}</span>
              </Button>
            </Badge>
          </li>
        ))}
        {pending.map(text => (
          <li key={`pending-${text}`}>
            <Badge
              variant="outline"
              className="flex items-center gap-1.5 border-dashed py-1 px-3 text-sm"
            >
              {text}
              <span className="text-xs font-normal text-muted-foreground">
                · Suggested — awaiting review
              </span>
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
};

export const SkillsAutocomplete: React.FC<SkillsAutocompleteProps> = ({
  selectedSkills,
  onSkillsChange,
  onSearchSkills,
  maxSkills = 10,
  placeholder = "Search for skills...",
  className,
  label = "Skills",
  showCount = true,
  hint,
  selectedLabel = "Selected Skills",
  disabled = false,
  showSelectedList = true,
  noMatchAction,
  pendingSuggestions = [],
}) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SkillSearchResult[]>([]);
  /** The query `results` belong to; the no-match action waits for it. */
  const [searchedQuery, setSearchedQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [actionBusy, setActionBusy] = useState(false);
  const [similar, setSimilar] = useState<{ name: string; skills: Skill[] } | null>(null);
  const [statusMessage, setStatusMessage] = useState("");

  const baseId = useId();
  const inputId = `${baseId}-input`;
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const trimmed = query.trim();

  // Debounced server search. The server ranks names and aliases; the only
  // thing filtered here is what is already selected — a name filter would
  // throw away every alias match (#1061).
  useEffect(() => {
    if (!trimmed) {
      // A search cancelled mid-flight never resets its own spinner.
      setIsLoading(false);
      setResults([]);
      setSearchedQuery("");
      setOpen(false);
      return;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        setIsLoading(true);
        const found = await onSearchSkills(trimmed);
        if (cancelled) {
          return;
        }
        setResults(found);
        setSearchedQuery(trimmed);
        setOpen(true);
        setHighlightedIndex(-1);
      } catch (error) {
        console.error("Failed to search skills:", error);
        if (!cancelled) {
          setResults([]);
          setSearchedQuery(trimmed);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmed, onSearchSkills]);

  // Close the list when clicking outside the picker.
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isMaxSkillsReached = selectedSkills.length >= maxSkills;
  const selectedIds = new Set(selectedSkills.map(s => s.id));
  const suggestions = results.filter(skill => !selectedIds.has(skill.id));

  // Offer the action once the search for this text has answered and no
  // result — selected or not — already is the typed skill, by name or alias.
  const normalizedQuery = normalizeSkillText(trimmed);
  const exactExists =
    results.some(
      s =>
        s.match_type === "name_exact" ||
        s.match_type === "alias_exact" ||
        normalizeSkillText(s.name) === normalizedQuery
    ) ||
    selectedSkills.some(s => normalizeSkillText(s.name) === normalizedQuery) ||
    pendingSuggestions.some(t => normalizeSkillText(t) === normalizedQuery);
  const showAction =
    !!noMatchAction &&
    !!normalizedQuery &&
    searchedQuery === trimmed &&
    !isLoading &&
    !exactExists &&
    !isMaxSkillsReached;
  const optionCount = suggestions.length + (showAction ? 1 : 0);
  const listOpen = open && optionCount > 0 && !disabled;

  // Keep the highlighted option visible while arrowing through a long list.
  useEffect(() => {
    if (highlightedIndex < 0) {
      return;
    }
    const el = document.getElementById(`${baseId}-option-${highlightedIndex}`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [highlightedIndex, baseId]);

  const resetSearch = () => {
    setQuery("");
    setResults([]);
    setSearchedQuery("");
    setOpen(false);
    setHighlightedIndex(-1);
  };

  const addSkill = (skill: Skill) => {
    if (isMaxSkillsReached) {
      return;
    }
    if (!selectedIds.has(skill.id)) {
      onSkillsChange([...selectedSkills, skill]);
    }
    resetSearch();
    inputRef.current?.focus();
  };

  const removeSkill = (skillId: number) => {
    onSkillsChange(selectedSkills.filter(skill => skill.id !== skillId));
  };

  const runAction = async () => {
    if (!noMatchAction || actionBusy) {
      return;
    }
    const text = trimmed;
    setActionBusy(true);
    setOpen(false);
    try {
      if (noMatchAction.kind === "suggest") {
        const outcome = await noMatchAction.suggest(text);
        resetSearch();
        if (outcome.status === "existing") {
          const already = selectedIds.has(outcome.skill.id);
          if (!already) {
            addSkill(outcome.skill);
          }
          setStatusMessage(
            already
              ? `"${text}" is ${outcome.skill.name}, which you already have.`
              : `"${text}" is already a skill: added ${outcome.skill.name} instead.`
          );
        } else {
          setStatusMessage(
            outcome.alreadySuggested
              ? `You have already suggested "${outcome.text}". It is awaiting review.`
              : `Thanks: "${outcome.text}" was sent to BondIT for review.`
          );
        }
      } else {
        const found = (await noMatchAction.findSimilar(text)).filter(s => !selectedIds.has(s.id));
        if (found.length > 0) {
          setSimilar({ name: text, skills: found });
        } else {
          await createSkill(text);
        }
      }
    } catch (error) {
      // The API client has already shown the error toast.
      console.warn("Skill picker action failed:", error);
    } finally {
      setActionBusy(false);
    }
  };

  const createSkill = async (name: string) => {
    if (noMatchAction?.kind !== "create") {
      return;
    }
    const created = await noMatchAction.create(name);
    setSimilar(null);
    addSkill(created);
    setStatusMessage(`Created skill "${created.name}".`);
  };

  const confirmCreate = async () => {
    if (!similar) {
      return;
    }
    setActionBusy(true);
    try {
      await createSkill(similar.name);
    } catch (error) {
      console.warn("Skill create failed:", error);
    } finally {
      setActionBusy(false);
    }
  };

  const pickSimilar = (skill: Skill) => {
    setSimilar(null);
    addSkill(skill);
  };

  const selectIndex = (index: number) => {
    if (index < suggestions.length) {
      addSkill(suggestions[index]);
    } else if (showAction) {
      void runAction();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        if (!listOpen) {
          setOpen(true);
          return;
        }
        setHighlightedIndex(prev => (prev < optionCount - 1 ? prev + 1 : prev));
        break;
      case "ArrowUp":
        e.preventDefault();
        setHighlightedIndex(prev => (prev > 0 ? prev - 1 : prev));
        break;
      case "Enter":
        // Never submit a surrounding form from the search field.
        e.preventDefault();
        if (listOpen && highlightedIndex >= 0 && highlightedIndex < optionCount) {
          selectIndex(highlightedIndex);
        }
        break;
      case "Escape":
        if (listOpen) {
          e.preventDefault();
          setOpen(false);
          setHighlightedIndex(-1);
        }
        break;
    }
  };

  const actionLabel =
    noMatchAction?.kind === "create"
      ? `Create skill "${trimmed}"`
      : `Can't find "${trimmed}"? Suggest it`;

  return (
    <div className={cn("space-y-4", className)}>
      <div className="space-y-2" ref={containerRef}>
        <Label htmlFor={inputId} className="flex items-center gap-2">
          <Search className="w-4 h-4" aria-hidden="true" />
          {label}
          {showCount && ` (${selectedSkills.length}/${maxSkills})`}
        </Label>

        <div className="relative">
          <Input
            ref={inputRef}
            id={inputId}
            type="text"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={listOpen}
            aria-controls={listboxId}
            aria-activedescendant={
              listOpen && highlightedIndex >= 0 ? optionId(highlightedIndex) : undefined
            }
            autoComplete="off"
            value={query}
            onChange={e => {
              setQuery(e.target.value);
              setStatusMessage("");
              setSimilar(null);
            }}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (trimmed && optionCount > 0) {
                setOpen(true);
              }
            }}
            placeholder={isMaxSkillsReached ? "Maximum skills reached" : placeholder}
            disabled={isMaxSkillsReached || disabled}
            className="pr-10"
          />

          <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
            {isLoading || actionBusy ? (
              <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" aria-hidden="true" />
            ) : (
              <Search className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
            )}
          </div>

          <ul
            id={listboxId}
            role="listbox"
            aria-label={`${label} matching "${trimmed}"`}
            hidden={!listOpen}
            className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-popover p-1 shadow-lg"
          >
            {listOpen &&
              suggestions.map((skill, index) => (
                <li
                  key={skill.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === highlightedIndex}
                  // Keep focus in the input so typing can continue.
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => addSkill(skill)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={cn(
                    "group flex min-h-10 cursor-pointer items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left",
                    index === highlightedIndex && "bg-accent text-accent-foreground"
                  )}
                >
                  <div className="min-w-0">
                    <div className="font-medium break-words">
                      <Highlighted text={skill.name} query={trimmed} />
                    </div>
                    {skill.matched_alias ? (
                      <div className="text-xs text-muted-foreground">
                        matched &ldquo;
                        <Highlighted text={skill.matched_alias} query={trimmed} />
                        &rdquo;
                      </div>
                    ) : (
                      skill.category && (
                        <div className="text-xs text-muted-foreground">{skill.category}</div>
                      )
                    )}
                  </div>
                  <Plus
                    className="w-4 h-4 shrink-0 opacity-0 transition-opacity group-hover:opacity-100"
                    aria-hidden="true"
                  />
                </li>
              ))}
            {listOpen && showAction && (
              <li
                id={optionId(suggestions.length)}
                role="option"
                aria-selected={highlightedIndex === suggestions.length}
                onMouseDown={e => e.preventDefault()}
                onClick={() => void runAction()}
                onMouseEnter={() => setHighlightedIndex(suggestions.length)}
                className={cn(
                  "flex min-h-10 cursor-pointer items-center gap-2 rounded-sm border-t border-border px-2 py-1.5 text-sm text-primary",
                  highlightedIndex === suggestions.length && "bg-accent text-accent-foreground"
                )}
              >
                {noMatchAction?.kind === "create" ? (
                  <Plus className="w-4 h-4 shrink-0" aria-hidden="true" />
                ) : (
                  <Lightbulb className="w-4 h-4 shrink-0" aria-hidden="true" />
                )}
                <span className="break-words">{actionLabel}</span>
              </li>
            )}
          </ul>
        </div>

        {similar && (
          <SimilarSkillsWarning
            name={similar.name}
            skills={similar.skills}
            busy={actionBusy}
            onUse={pickSimilar}
            onCreate={confirmCreate}
            onCancel={() => setSimilar(null)}
          />
        )}

        <p className="text-xs text-muted-foreground" role="status" aria-live="polite">
          {statusMessage ||
            hint ||
            `Type to search skills by name or alias. You can select up to ${maxSkills} skills.`}
        </p>
      </div>

      {showSelectedList && (
        <SelectedSkillChips
          label={selectedLabel}
          skills={selectedSkills}
          pending={pendingSuggestions}
          disabled={disabled}
          onRemove={removeSkill}
        />
      )}

      {/* Max skills warning */}
      {isMaxSkillsReached && (
        <p className="text-sm text-orange-600 dark:text-orange-400">
          You've reached the maximum of {maxSkills} skills. Remove some skills to add new ones.
        </p>
      )}
    </div>
  );
};
